import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';

const MetricCard = ({ title, value, subtitle, color = '#1DB954' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-2">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        <div className="text-white text-[18px] font-bold mb-1">{value}</div>
        {subtitle && (
            <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
        )}
    </div>
);

const ContentCard = ({ title, children }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-4">
            <div className="w-[8px] h-[8px] rounded-full bg-[#1DB954]" />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        {children}
    </div>
);

const PlaylistingAnalytics = () => {
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('week');
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadAnalytics();
    }, [filter]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await getDataAPI(`user/analytics/playlisting?filter=${filter}`, token);
            setAnalyticsData(response.data.data.response);
        } catch (error) {
            console.error('Failed to load playlisting analytics:', error);
            setAnalyticsData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (!num || isNaN(num)) return '0';
        return new Intl.NumberFormat('en-US').format(num);
    };

    const formatFollowers = (num) => {
        if (!num || isNaN(num)) return '0';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return formatNumber(num);
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
                    <p className="text-[12px]">Loading playlisting analytics...</p>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] h-screen bg-black text-white relative">
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <div className="flex items-center gap-2">
                            <div 
                                className="w-[24px] h-[24px] rounded-md flex items-center justify-center text-[8px] font-medium text-white"
                                style={{ backgroundColor: '#1DB954' }}
                            >
                                PL
                            </div>
                            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Playlisting Analytics</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className='text-[red] cursor-pointer text-[12px]'>Export data</p>
                            <div 
                                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                                onClick={() => navigate('/analytics/hub')}
                            >
                                Back
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
                        {/* Filter */}
                        <div className="mb-6">
                            <select 
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="bg-[#1A1A1A] border border-[#333] text-white text-[12px] px-3 py-2 rounded"
                            >
                                <option value="day">Last 24 Hours</option>
                                <option value="week">Last Week</option>
                                <option value="month">Last Month</option>
                                <option value="year">Last Year</option>
                                <option value="all">All Time</option>
                            </select>
                        </div>

                        {analyticsData ? (
                            <div className="space-y-4">
                                {/* Top Metrics Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="New Playlist Adds"
                                        value={formatNumber(analyticsData.newPlaylistAdds || 0)}
                                        subtitle="Recent additions"
                                    />
                                    <MetricCard
                                        title="Total Playlists"
                                        value={formatNumber(analyticsData.totalPlaylists || 0)}
                                        subtitle="Active playlists"
                                    />
                                </div>

                                {/* Second Metrics Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    <MetricCard
                                        title="Reach"
                                        value={formatFollowers(analyticsData.totalReach || 0)}
                                        subtitle="Combined followers"
                                    />
                                    <MetricCard
                                        title="Avg Playlist Size"
                                        value={formatFollowers(analyticsData.avgPlaylistSize || 0)}
                                        subtitle="Followers per playlist"
                                    />
                                </div>

                                {/* Top Playlists */}
                                <ContentCard title="Top Performing Playlists">
                                    <div className="space-y-3">
                                        {analyticsData.topPlaylists && analyticsData.topPlaylists.length > 0 ? (
                                            analyticsData.topPlaylists.map((playlist, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-medium">{playlist.name}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                            {playlist.songTitle} • {playlist.curator}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[#1DB954] text-[12px] font-bold">{formatFollowers(playlist.followers)}</div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">followers</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No playlist data available</p>
                                        )}
                                    </div>
                                </ContentCard>

                                {/* Recent Additions */}
                                <ContentCard title="Recent Playlist Additions">
                                    <div className="space-y-3">
                                        {analyticsData.recentAdditions && analyticsData.recentAdditions.length > 0 ? (
                                            analyticsData.recentAdditions.map((addition, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-medium">{addition.playlistName}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                            {addition.songTitle} • Added {addition.dateAdded}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[#1DB954] text-[12px] font-bold">{formatFollowers(addition.followers)}</div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">followers</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No recent additions</p>
                                        )}
                                    </div>
                                </ContentCard>

                                {/* Playlist Categories */}
                                <ContentCard title="Top Playlist Categories">
                                    <div className="space-y-3">
                                        {analyticsData.categories && analyticsData.categories.length > 0 ? (
                                            analyticsData.categories.map((category, index) => (
                                                <div key={index} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.1)] last:border-b-0">
                                                    <div className="flex-1">
                                                        <div className="text-white text-[12px] font-medium">{category.name}</div>
                                                        <div className="text-[rgba(255,255,255,0.7)] text-[10px]">{category.playlistCount} playlists</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[#1DB954] text-[12px] font-bold">{formatFollowers(category.totalReach)}</div>
                                                        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">total reach</div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No category data available</p>
                                        )}
                                    </div>
                                </ContentCard>
                            </div>
                        ) : (
                            <div className="text-center py-10">
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px]">No playlisting data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default PlaylistingAnalytics; 